import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'

/**
 * Auth store — manages authentication state.
 *
 * Persisted to localStorage under 'agrocast_auth'.
 * The access token is stored separately in localStorage
 * (set by apiClient interceptors), not here — only non-sensitive
 * session state lives in this store.
 */
const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ── State ──────────────────────────────────────
        user:            null,
        isAuthenticated: false,
        isInitializing:  true,

        // ── Actions ────────────────────────────────────
        setUser: (user) => set({ user, isAuthenticated: !!user }),

        setTokens: ({ accessToken, refreshToken }) => {
          localStorage.setItem('agrocast_access_token', accessToken)
          localStorage.setItem('agrocast_refresh_token', refreshToken)
        },

        login: ({ user, accessToken, refreshToken }) => {
          localStorage.setItem('agrocast_access_token', accessToken)
          localStorage.setItem('agrocast_refresh_token', refreshToken)
          set({ user, isAuthenticated: true })
        },

        logout: () => {
          localStorage.removeItem('agrocast_access_token')
          localStorage.removeItem('agrocast_refresh_token')
          localStorage.removeItem('agrocast_session_user_id') // session key used by authService.local
          localStorage.removeItem('agrocast_demo_user_id')    // legacy: clean up renamed key on next logout
          localStorage.removeItem('aftersubmit_session_user') // API session persistence key
          set({ user: null, isAuthenticated: false })
        },

        setInitializing: (value) => set({ isInitializing: value }),

        // ── Selectors ──────────────────────────────────
        getUser:        () => get().user,
        isLoggedIn:     () => get().isAuthenticated,
        getPermissions: () => get().user?.permissions ?? [],
        getRole:        () => get().user?.role ?? null,
      }),
      {
        name:    'agrocast_auth',
        partialize: (state) => ({
          user:            state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
)
export { useAuthStore }
export default useAuthStore
